import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import {
  FileText, Plus, Search, Trash2, Edit, Receipt, Calendar, Building2,
  DollarSign, RefreshCw, X, ChevronDown, ChevronRight, Eye, Copy, BookTemplate, Save, LayoutTemplate, AlertTriangle, RotateCcw, ExternalLink
} from 'lucide-react';
import { format, addYears, differenceInDays, parseISO } from 'date-fns';
import { formatDateOnly } from '../utils/timezone';
import { isAgreementArchived, shouldShowRenew } from '../utils/agreementScope';

const FREQ_LABELS = {
  'one-time': 'One-Time',
  'monthly': 'Monthly',
  'quarterly': 'Quarterly',
  'semi-annual': 'Semi-Annual',
  'annual': 'Annual',
};

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  expired: 'bg-orange-100 text-orange-700',
  cancelled: 'bg-red-100 text-red-700',
};

const emptyLineItem = () => ({ description: '', quantity: 1, unit_price: 0, amount: 0 });

export default function Agreements() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [agreements, setAgreements] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [viewScope, setViewScope] = useState('current'); // 'current' | 'all' | 'archived'
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [detailInvoices, setDetailInvoices] = useState([]);
  const [generating, setGenerating] = useState(null);
  const [activeTab, setActiveTab] = useState('agreements'); // 'agreements' | 'templates'
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', type: 'MSA', description: '', billing_frequency: 'monthly', payment_terms_days: 30, line_items: [{ description: '', quantity: 1, unit_price: 0 }], tax_rate: 0, notes: '' });
  const [renewals, setRenewals] = useState([]);
  const [renewalBannerOpen, setRenewalBannerOpen] = useState(true);
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [renewingAgr, setRenewingAgr] = useState(null);
  const [renewEndDate, setRenewEndDate] = useState('');
  const [renewing, setRenewing] = useState(false);

  const defaultForm = {
    customer_id: searchParams.get('customer_id') || '',
    type: 'MSA',
    name: '',
    description: '',
    status: 'active',
    start_date: new Date().toISOString().slice(0, 10),
    end_date: '',
    billing_frequency: 'monthly',
    payment_terms_days: 30,
    line_items: [emptyLineItem()],
    tax_rate: 0,
    auto_invoice: false,
    notes: '',
    sla_response_hours: '',
    sla_resolution_hours: '',
  };
  const [form, setForm] = useState(defaultForm);

  const fetchData = useCallback(async () => {
    try {
      const [agrRes, custRes, tmplRes, renewRes] = await Promise.all([
        api.get('/agreements'),
        api.get('/customers?limit=500'),
        api.get('/agreement-templates'),
        api.get('/agreements/renewals/upcoming').catch(() => ({ data: [] })),
      ]);
      setAgreements(agrRes.data);
      setCustomers(custRes.data.customers || custRes.data || []);
      setTemplates(tmplRes.data);
      setRenewals(renewRes.data);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const calcTotals = (items, taxRate) => {
    const subtotal = items.reduce((sum, i) => sum + (i.quantity * i.unit_price), 0);
    const taxAmount = subtotal * (taxRate / 100);
    return { subtotal: Math.round(subtotal * 100) / 100, tax_amount: Math.round(taxAmount * 100) / 100, total: Math.round((subtotal + taxAmount) * 100) / 100 };
  };

  const updateLineItem = (idx, field, value) => {
    const items = [...form.line_items];
    items[idx] = { ...items[idx], [field]: value };
    items[idx].amount = Math.round(items[idx].quantity * items[idx].unit_price * 100) / 100;
    setForm(f => ({ ...f, line_items: items }));
  };

  const addLineItem = () => setForm(f => ({ ...f, line_items: [...f.line_items, emptyLineItem()] }));
  const removeLineItem = (idx) => setForm(f => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));

  const openCreate = () => {
    setEditingId(null);
    setForm(defaultForm);
    setDialogOpen(true);
  };

  const openEdit = (agr) => {
    setEditingId(agr.id);
    setForm({
      customer_id: agr.customer_id,
      type: agr.type,
      name: agr.name,
      description: agr.description || '',
      status: agr.status,
      start_date: agr.start_date || '',
      end_date: agr.end_date || '',
      billing_frequency: agr.billing_frequency,
      payment_terms_days: agr.payment_terms_days || 30,
      line_items: agr.line_items?.length ? agr.line_items : [emptyLineItem()],
      tax_rate: agr.tax_rate || 0,
      auto_invoice: agr.auto_invoice || false,
      notes: agr.notes || '',
      sla_response_hours: agr.sla_response_hours ?? '',
      sla_resolution_hours: agr.sla_resolution_hours ?? '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.customer_id || !form.name || !form.start_date) {
      toast.error('Customer, name, and start date are required');
      return;
    }
    const totals = calcTotals(form.line_items, form.tax_rate);
    const customer = customers.find(c => c.id === form.customer_id);
    const payload = {
      ...form,
      customer_name: customer?.company_name || '',
      ...totals,
      line_items: form.line_items.map(i => ({ ...i, amount: Math.round(i.quantity * i.unit_price * 100) / 100 })),
      sla_response_hours: form.sla_response_hours !== '' ? parseFloat(form.sla_response_hours) : null,
      sla_resolution_hours: form.sla_resolution_hours !== '' ? parseFloat(form.sla_resolution_hours) : null,
    };
    try {
      if (editingId) {
        await api.put(`/agreements/${editingId}`, payload);
        toast.success('Agreement updated');
      } else {
        await api.post('/agreements', payload);
        toast.success('Agreement created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this agreement? This cannot be undone.')) return;
    try {
      await api.delete(`/agreements/${id}`);
      toast.success('Agreement deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleGenerateInvoice = async (agr) => {
    if (!window.confirm(`Generate invoice for "${agr.name}"?`)) return;
    setGenerating(agr.id);
    try {
      const res = await api.post(`/agreements/${agr.id}/generate-invoice`);
      const inv = res.data.invoice;
      toast.success(`Invoice #${inv.invoice_number} generated ($${inv.total})`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGenerating(null);
    }
  };

  const openDetail = async (agr) => {
    setDetailId(agr.id);
    try {
      const res = await api.get(`/agreements/${agr.id}/invoices`);
      setDetailInvoices(res.data);
    } catch {
      setDetailInvoices([]);
    }
  };

  // Template handlers
  const loadFromTemplate = (tmpl) => {
    setForm(f => ({
      ...f,
      type: tmpl.type,
      name: tmpl.name,
      description: tmpl.description || '',
      billing_frequency: tmpl.billing_frequency,
      payment_terms_days: tmpl.payment_terms_days || 30,
      line_items: tmpl.line_items?.length ? tmpl.line_items.map(i => ({ description: i.description, quantity: i.quantity, unit_price: i.unit_price, amount: i.amount || i.quantity * i.unit_price })) : [emptyLineItem()],
      tax_rate: tmpl.tax_rate || 0,
      notes: tmpl.notes || '',
    }));
    toast.success(`Template "${tmpl.name}" loaded`);
  };

  const saveAsTemplate = async () => {
    const name = window.prompt('Template name:', form.name || 'New Template');
    if (!name) return;
    try {
      await api.post('/agreement-templates', {
        name,
        type: form.type,
        description: form.description,
        billing_frequency: form.billing_frequency,
        payment_terms_days: form.payment_terms_days,
        line_items: form.line_items.map(i => ({ description: i.description, quantity: i.quantity, unit_price: i.unit_price })),
        tax_rate: form.tax_rate,
        notes: form.notes,
      });
      toast.success(`Template "${name}" saved`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    }
  };

  const openTemplateCreate = () => {
    setEditingTemplateId(null);
    setTemplateForm({ name: '', type: 'MSA', description: '', billing_frequency: 'monthly', payment_terms_days: 30, line_items: [{ description: '', quantity: 1, unit_price: 0 }], tax_rate: 0, notes: '' });
    setTemplateDialogOpen(true);
  };

  const openTemplateEdit = (tmpl) => {
    setEditingTemplateId(tmpl.id);
    setTemplateForm({
      name: tmpl.name,
      type: tmpl.type,
      description: tmpl.description || '',
      billing_frequency: tmpl.billing_frequency,
      payment_terms_days: tmpl.payment_terms_days || 30,
      line_items: tmpl.line_items?.length ? tmpl.line_items.map(i => ({ description: i.description, quantity: i.quantity, unit_price: i.unit_price })) : [{ description: '', quantity: 1, unit_price: 0 }],
      tax_rate: tmpl.tax_rate || 0,
      notes: tmpl.notes || '',
    });
    setTemplateDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim()) { toast.error('Template name is required'); return; }
    try {
      if (editingTemplateId) {
        await api.put(`/agreement-templates/${editingTemplateId}`, templateForm);
        toast.success('Template updated');
      } else {
        await api.post('/agreement-templates', templateForm);
        toast.success('Template created');
      }
      setTemplateDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/agreement-templates/${id}`);
      toast.success('Template deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete template');
    }
  };

  const updateTemplateLineItem = (idx, field, value) => {
    const items = [...templateForm.line_items];
    items[idx] = { ...items[idx], [field]: value };
    setTemplateForm(f => ({ ...f, line_items: items }));
  };

  const templateTotals = (() => {
    const sub = templateForm.line_items.reduce((s, i) => s + (i.quantity * i.unit_price), 0);
    const tax = sub * (templateForm.tax_rate / 100);
    return { subtotal: Math.round(sub * 100) / 100, total: Math.round((sub + tax) * 100) / 100 };
  })();

  // Renewal handlers
  const openRenewDialog = (agr) => {
    setRenewingAgr(agr);
    // Auto-calculate: extend by same duration
    if (agr.start_date && agr.end_date) {
      try {
        const start = parseISO(agr.start_date);
        const end = parseISO(agr.end_date);
        const durationMs = end.getTime() - start.getTime();
        const newEnd = new Date(end.getTime() + durationMs);
        setRenewEndDate(format(newEnd, 'yyyy-MM-dd'));
      } catch {
        setRenewEndDate(format(addYears(new Date(), 1), 'yyyy-MM-dd'));
      }
    } else {
      setRenewEndDate(format(addYears(new Date(), 1), 'yyyy-MM-dd'));
    }
    setRenewDialogOpen(true);
  };

  const handleRenew = async () => {
    if (!renewingAgr || !renewEndDate) return;
    setRenewing(true);
    try {
      await api.post(`/agreements/${renewingAgr.id}/renew`, { new_end_date: renewEndDate });
      toast.success(`Agreement "${renewingAgr.name}" renewed until ${renewEndDate}`);
      setRenewDialogOpen(false);
      setRenewingAgr(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to renew agreement');
    } finally {
      setRenewing(false);
    }
  };

  const urgencyColors = {
    overdue: { bg: 'bg-red-50 dark:bg-red-950/40', border: 'border-red-200 dark:border-red-800', text: 'text-red-700 dark:text-red-300', badge: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-200' },
    urgent: { bg: 'bg-orange-50 dark:bg-orange-950/40', border: 'border-orange-200 dark:border-orange-800', text: 'text-orange-700 dark:text-orange-300', badge: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-200' },
    upcoming: { bg: 'bg-yellow-50 dark:bg-yellow-950/40', border: 'border-yellow-200 dark:border-yellow-800', text: 'text-yellow-700 dark:text-yellow-300', badge: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-200' },
  };

  // Current vs Archived classification lives in utils/agreementScope (shared with
  // list, counts, renewal banner and actions). Archived = expired/cancelled/
  // deactivated status, is_active === false, or Central calendar end_date elapsed.
  const isArchivedAgreement = isAgreementArchived;
  const scopeAgreements = agreements.filter(a => {
    if (viewScope === 'archived') return isArchivedAgreement(a);
    if (viewScope === 'current') return !isArchivedAgreement(a);
    return true; // 'all'
  });
  // Current scope hides renewals for agreements that have already lapsed on the
  // calendar / been deactivated; All & Archived keep them for history + renewal.
  const visibleRenewals = viewScope === 'current'
    ? renewals.filter(r => !isArchivedAgreement(r))
    : renewals;

  const filtered = scopeAgreements.filter(a => {
    if (filterStatus && a.status !== filterStatus) return false;
    if (filterType && a.type !== filterType) return false;
    if (search) {
      const s = search.toLowerCase();
      return (a.name?.toLowerCase().includes(s) || a.customer_name?.toLowerCase().includes(s));
    }
    return true;
  });

  const totals = calcTotals(form.line_items, form.tax_rate);
  const detailAgr = agreements.find(a => a.id === detailId);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  }

  return (
    <div data-testid="agreements-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="page-title">
            <FileText className="w-6 h-6 text-blue-600" />
            Agreements
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage SLA and MSA agreements with invoice generation</p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'templates' ? (
            <Button onClick={openTemplateCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="create-template-btn">
              <Plus className="w-4 h-4 mr-2" /> New Template
            </Button>
          ) : (
            <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="create-agreement-btn">
              <Plus className="w-4 h-4 mr-2" /> New Agreement
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('agreements')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'agreements' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-agreements"
        >
          <FileText className="w-4 h-4 inline mr-1.5" />Agreements ({agreements.length})
        </button>
        <button
          onClick={() => setActiveTab('templates')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'templates' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-templates"
        >
          <LayoutTemplate className="w-4 h-4 inline mr-1.5" />Templates ({templates.length})
        </button>
      </div>

      {activeTab === 'agreements' && (<>

      {/* Current / All / Archived scope */}
      <div className="flex items-center gap-1 mb-5 flex-wrap" data-testid="agreement-scope-selector">
        {[
          { key: 'current', label: 'Current', hint: 'Active & draft agreements' },
          { key: 'all', label: 'All', hint: 'Every agreement' },
          { key: 'archived', label: 'Archived', hint: 'Expired & cancelled — renew or view history' },
        ].map(s => (
          <button
            key={s.key}
            onClick={() => { setViewScope(s.key); setFilterStatus(''); }}
            title={s.hint}
            className={`px-3.5 py-1.5 text-sm font-medium rounded-full border transition-colors ${viewScope === s.key ? 'bg-blue-600 border-blue-600 text-white' : 'bg-transparent border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800'}`}
            data-testid={`scope-${s.key}`}
          >
            {s.label} ({s.key === 'current' ? agreements.filter(a => !isArchivedAgreement(a)).length : s.key === 'archived' ? agreements.filter(a => isArchivedAgreement(a)).length : agreements.length})
          </button>
        ))}
      </div>

      {/* Renewal Alert Banner */}
      {viewScope !== 'archived' && visibleRenewals.length > 0 && renewalBannerOpen && (
        <div className="renewal-banner mb-6 border border-orange-200 bg-orange-50 rounded-lg overflow-hidden dark:border-orange-800 dark:bg-orange-950/40" data-testid="renewal-banner">
          <button
            className="renewal-banner-toggle w-full flex items-center justify-between px-4 py-3 hover:bg-orange-100 transition-colors dark:hover:bg-orange-900/40"
            onClick={() => setRenewalBannerOpen(p => !p)}
            data-testid="renewal-banner-toggle"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="renewal-icon w-5 h-5 text-orange-600 dark:text-orange-300" />
              <span className="renewal-head text-sm font-semibold text-orange-800 dark:text-orange-200">
                {visibleRenewals.length} agreement{visibleRenewals.length > 1 ? 's' : ''} approaching renewal
              </span>
              <span className="renewal-sub text-xs text-orange-600 dark:text-orange-300">
                ({visibleRenewals.filter(r => r.renewal_urgency === 'overdue').length} overdue, {visibleRenewals.filter(r => r.renewal_urgency === 'urgent').length} urgent)
              </span>
            </div>
            <ChevronDown className="renewal-icon w-4 h-4 text-orange-500 dark:text-orange-300" />
          </button>
          <div className="divide-y divide-orange-200 dark:divide-orange-800">
            {visibleRenewals.map(r => {
              const colors = urgencyColors[r.renewal_urgency] || urgencyColors.upcoming;
              const urg = r.renewal_urgency || 'upcoming';
              return (
                <div key={r.id} className={`renewal-row renewal-row--${urg} flex items-center justify-between px-4 py-2.5 ${colors.bg}`} data-testid={`renewal-item-${r.id}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <Badge className={`renewal-badge renewal-badge--${urg} text-[10px] ${colors.badge}`}>
                      {r.renewal_urgency === 'overdue' ? 'Overdue' : r.renewal_urgency === 'urgent' ? 'Urgent' : 'Upcoming'}
                    </Badge>
                    <div className="min-w-0">
                      <span className="renewal-name text-sm font-medium text-gray-900 dark:text-neutral-100 truncate block">{r.name}</span>
                      <span className="renewal-meta text-xs text-gray-500 dark:text-neutral-400">{r.customer_name} — expires {formatDateOnly(r.end_date)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                    <span className={`renewal-days renewal-days--${urg} text-xs font-medium ${colors.text}`}>
                      {r.days_until_expiry < 0 ? `${Math.abs(r.days_until_expiry)}d overdue` : `${r.days_until_expiry}d left`}
                    </span>
                    <Button
                      size="sm" variant="outline"
                      onClick={(e) => { e.stopPropagation(); openRenewDialog(r); }}
                      className="renewal-renew-btn text-green-700 border-green-300 hover:bg-green-50 dark:text-green-300 dark:border-green-700 dark:hover:bg-green-900/40"
                      data-testid={`renew-btn-${r.id}`}
                    >
                      <RotateCcw className="h-3.5 w-3.5 mr-1" /> Renew
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Card><CardContent className="p-4">
          <div className="text-sm text-gray-500">{viewScope === 'archived' ? 'Archived' : viewScope === 'all' ? 'Total' : 'Current'}</div>
          <div className="text-2xl font-bold" data-testid="stat-total">{scopeAgreements.length}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-sm text-gray-500">Active</div>
          <div className="text-2xl font-bold text-green-600">{scopeAgreements.filter(a => a.status === 'active').length}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-sm text-gray-500">Monthly Revenue</div>
          <div className="text-2xl font-bold text-blue-600">${scopeAgreements.filter(a => a.status === 'active' && a.billing_frequency === 'monthly').reduce((s, a) => s + (a.total || 0), 0).toLocaleString()}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-sm text-gray-500">Auto-Invoice</div>
          <div className="text-2xl font-bold text-purple-600">{scopeAgreements.filter(a => a.auto_invoice).length}</div>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Search by name or customer..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="search-agreements" />
        </div>
        <Select value={filterStatus || 'all'} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[150px]" data-testid="agreement-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{viewScope === 'archived' ? 'All Archived' : viewScope === 'current' ? 'All Current' : 'All Status'}</SelectItem>
            {viewScope !== 'archived' && <SelectItem value="active">Active</SelectItem>}
            {viewScope !== 'archived' && <SelectItem value="draft">Draft</SelectItem>}
            {viewScope !== 'current' && <SelectItem value="expired">Expired</SelectItem>}
            {viewScope !== 'current' && <SelectItem value="cancelled">Cancelled</SelectItem>}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={v => setFilterType(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[130px]"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="SLA">SLA</SelectItem>
            <SelectItem value="MSA">MSA</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-600">
            {agreements.length === 0 ? 'No agreements yet' : viewScope === 'archived' ? 'No archived agreements' : viewScope === 'current' ? 'No current agreements' : 'No results'}
          </h3>
          <p className="text-sm text-gray-400 mt-1">
            {viewScope === 'archived' ? 'Expired and cancelled agreements will appear here. You can still renew them or review their history.' : 'Create your first SLA or MSA agreement to get started.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="agreements-table">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3">Agreement</th>
                  <th className="px-4 py-3 hidden sm:table-cell">Customer</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3 hidden md:table-cell">Billing</th>
                  <th className="px-4 py-3 hidden md:table-cell">Total</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Next Invoice</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(agr => (
                  <tr key={agr.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => openDetail(agr)} data-testid={`agreement-row-${agr.id}`}>
                    <td className="px-4 py-3">
                      <div className="text-left">
                        <div className="text-sm font-semibold text-gray-900">{agr.name}</div>
                        <div className="text-xs text-gray-500 sm:hidden">{agr.customer_name}</div>
                        <div className="text-xs text-gray-400">{agr.start_date} — {agr.end_date || 'Ongoing'}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell text-sm text-gray-700">{agr.customer_name}</td>
                    <td className="px-4 py-3">
                      <Badge className={`agr-type-badge agr-type-${agr.type} text-xs ${agr.type === 'SLA' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{agr.type}</Badge>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-sm text-gray-600">{FREQ_LABELS[agr.billing_frequency] || agr.billing_frequency}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-sm font-medium">${(agr.total || 0).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <Badge className={`text-xs ${STATUS_COLORS[agr.status] || 'bg-gray-100 text-gray-700'}`}>{agr.status}</Badge>
                      {agr.auto_invoice && <Badge className="agr-auto-badge ml-1 bg-purple-50 text-purple-600 text-[10px]">Auto</Badge>}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-sm text-gray-500">{agr.next_invoice_date || '—'}</td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {shouldShowRenew({ agreement: agr, viewScope, isUpcomingRenewal: renewals.some(r => r.id === agr.id) }) && (
                          <Button variant="ghost" size="sm" onClick={() => openRenewDialog(agr)} title="Renew Agreement" data-testid={`table-renew-btn-${agr.id}`}>
                            <RotateCcw className="h-4 w-4 text-orange-500" />
                          </Button>
                        )}
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => handleGenerateInvoice(agr)}
                          disabled={generating === agr.id || !['active', 'draft'].includes(agr.status)}
                          title="Generate Invoice"
                          data-testid={`generate-invoice-btn-${agr.id}`}
                        >
                          <Receipt className={`h-4 w-4 ${generating === agr.id ? 'animate-spin' : 'text-green-600'}`} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(agr)} title="Edit" data-testid={`edit-btn-${agr.id}`}>
                          <Edit className="h-4 w-4 text-blue-500" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(agr.id)} title="Delete" data-testid={`delete-btn-${agr.id}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </>)}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div>
          {templates.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <LayoutTemplate className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-600">No templates yet</h3>
              <p className="text-sm text-gray-400 mt-1">Create reusable templates to quickly spin up agreements.</p>
              <Button onClick={openTemplateCreate} className="mt-4 bg-blue-600 hover:bg-blue-700" data-testid="empty-create-template-btn">
                <Plus className="w-4 h-4 mr-2" /> Create First Template
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(tmpl => (
                <div key={tmpl.id} className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow" data-testid={`template-card-${tmpl.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-bold text-gray-900">{tmpl.name}</h3>
                      <Badge className={`mt-1 text-xs ${tmpl.type === 'SLA' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{tmpl.type}</Badge>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openTemplateEdit(tmpl)} title="Edit" data-testid={`edit-template-btn-${tmpl.id}`}>
                        <Edit className="h-3.5 w-3.5 text-blue-500" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteTemplate(tmpl.id)} title="Delete" data-testid={`delete-template-btn-${tmpl.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  {tmpl.description && <p className="text-xs text-gray-500 mb-3 line-clamp-2">{tmpl.description}</p>}
                  <div className="space-y-1 mb-3">
                    {tmpl.line_items?.map((item, i) => (
                      <div key={i} className="flex justify-between text-xs text-gray-600">
                        <span className="truncate mr-2">{item.description}</span>
                        <span className="font-medium flex-shrink-0">${item.amount || (item.quantity * item.unit_price)}</span>
                      </div>
                    ))}
                  </div>
                  <Separator className="my-3" />
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs text-gray-500">{FREQ_LABELS[tmpl.billing_frequency]}</span>
                      <span className="text-base font-bold text-gray-900 ml-2">${tmpl.total || 0}</span>
                    </div>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => { setActiveTab('agreements'); openCreate(); setTimeout(() => loadFromTemplate(tmpl), 100); }}
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      data-testid={`use-template-btn-${tmpl.id}`}
                    >
                      <Copy className="h-3.5 w-3.5 mr-1" /> Use
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="agreement-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Agreement' : 'Create Agreement'}</DialogTitle>
          </DialogHeader>

          {/* Template bar */}
          {!editingId && templates.length > 0 && (
            <div className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg border border-dashed border-gray-300 mt-1">
              <LayoutTemplate className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="text-xs text-gray-500 flex-shrink-0">Load from template:</span>
              <div className="flex gap-1.5 flex-wrap">
                {templates.map(t => (
                  <button
                    key={t.id}
                    onClick={() => loadFromTemplate(t)}
                    className="text-xs px-2.5 py-1 rounded-full bg-white border border-gray-200 text-gray-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors"
                    data-testid={`load-template-${t.id}`}
                  >
                    {t.name} (${t.total})
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Customer *</Label>
                <Select value={form.customer_id} onValueChange={v => setForm(f => ({ ...f, customer_id: v }))}>
                  <SelectTrigger data-testid="agreement-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type *</Label>
                <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger data-testid="agreement-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SLA">SLA (Service Level Agreement)</SelectItem>
                    <SelectItem value="MSA">MSA (Managed Service Agreement)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Agreement Name *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Managed IT Services" data-testid="agreement-name" />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="Agreement scope and details..." />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <Label>Start Date *</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} data-testid="agreement-start-date" />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
              </div>
              <div>
                <Label>Billing Frequency *</Label>
                <Select value={form.billing_frequency} onValueChange={v => setForm(f => ({ ...f, billing_frequency: v }))}>
                  <SelectTrigger data-testid="agreement-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one-time">One-Time</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="semi-annual">Semi-Annual</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment Terms (days)</Label>
                <Input type="number" value={form.payment_terms_days} onChange={e => setForm(f => ({ ...f, payment_terms_days: parseInt(e.target.value) || 30 }))} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>SLA Response Time (hours)</Label>
                <Input type="number" step="0.5" placeholder="e.g. 4" value={form.sla_response_hours} onChange={e => setForm(f => ({ ...f, sla_response_hours: e.target.value }))} data-testid="sla-response-hours" />
                <p className="text-xs text-gray-500 mt-1">Max hours for first response</p>
              </div>
              <div>
                <Label>SLA Resolution Time (hours)</Label>
                <Input type="number" step="0.5" placeholder="e.g. 24" value={form.sla_resolution_hours} onChange={e => setForm(f => ({ ...f, sla_resolution_hours: e.target.value }))} data-testid="sla-resolution-hours" />
                <p className="text-xs text-gray-500 mt-1">Max hours for full resolution</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.auto_invoice} onChange={e => setForm(f => ({ ...f, auto_invoice: e.target.checked }))} className="w-4 h-4 rounded border-gray-300" data-testid="auto-invoice-toggle" />
                  <span className="text-sm font-medium text-gray-700">Auto-generate invoices</span>
                </label>
              </div>
            </div>

            <Separator />

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold">Line Items</Label>
                <Button variant="outline" size="sm" onClick={addLineItem} data-testid="add-line-item-btn">
                  <Plus className="h-3 w-3 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-2">
                {form.line_items.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2" data-testid={`line-item-${idx}`}>
                    <Input
                      placeholder="Description"
                      value={item.description}
                      onChange={e => updateLineItem(idx, 'description', e.target.value)}
                      className="flex-1"
                      data-testid={`line-desc-${idx}`}
                    />
                    <Input
                      type="number" placeholder="Qty" value={item.quantity}
                      onChange={e => updateLineItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                      className="w-20"
                    />
                    <Input
                      type="number" placeholder="Price" value={item.unit_price}
                      onChange={e => updateLineItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                      className="w-24"
                      data-testid={`line-price-${idx}`}
                    />
                    <span className="text-sm font-medium w-20 text-right">${(item.quantity * item.unit_price).toFixed(2)}</span>
                    {form.line_items.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeLineItem(idx)}>
                        <X className="h-3 w-3 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Tax & Totals */}
            <div className="flex flex-col items-end gap-1 text-sm">
              <div className="flex items-center gap-3">
                <span className="text-gray-500">Tax Rate (%):</span>
                <Input type="number" value={form.tax_rate} onChange={e => setForm(f => ({ ...f, tax_rate: parseFloat(e.target.value) || 0 }))} className="w-20 h-8 text-right" />
              </div>
              <div className="flex gap-8 mt-1">
                <span className="text-gray-500">Subtotal: <strong>${totals.subtotal.toFixed(2)}</strong></span>
                {totals.tax_amount > 0 && <span className="text-gray-500">Tax: <strong>${totals.tax_amount.toFixed(2)}</strong></span>}
                <span className="text-gray-900 font-bold text-base">Total: ${totals.total.toFixed(2)}</span>
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Internal notes..." />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={saveAsTemplate} className="mr-auto text-gray-600" data-testid="save-as-template-btn">
                <Save className="h-3.5 w-3.5 mr-1.5" /> Save as Template
              </Button>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="save-agreement-btn">
                {editingId ? 'Update Agreement' : 'Create Agreement'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail / Invoice History Dialog */}
      <Dialog open={!!detailId} onOpenChange={open => { if (!open) setDetailId(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="agreement-detail-dialog">
          {detailAgr && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle className="flex items-center gap-2">
                    <Badge className={detailAgr.type === 'SLA' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>{detailAgr.type}</Badge>
                    {detailAgr.name}
                  </DialogTitle>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => { setDetailId(null); navigate(`/agreements/${detailAgr.id}`); }}
                    className="text-blue-600 border-blue-200 hover:bg-blue-50 flex-shrink-0"
                    data-testid="view-full-page-btn"
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Full Page
                  </Button>
                </div>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Customer</span>
                    <div className="font-medium">{detailAgr.customer_name}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Status</span>
                    <div><Badge className={`text-xs ${STATUS_COLORS[detailAgr.status]}`}>{detailAgr.status}</Badge></div>
                  </div>
                  <div>
                    <span className="text-gray-500">Billing</span>
                    <div className="font-medium">{FREQ_LABELS[detailAgr.billing_frequency]}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Period</span>
                    <div className="font-medium">{detailAgr.start_date} — {detailAgr.end_date || 'Ongoing'}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Total per Period</span>
                    <div className="font-bold text-lg">${(detailAgr.total || 0).toLocaleString()}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Next Invoice</span>
                    <div className="font-medium">{detailAgr.next_invoice_date || '—'}</div>
                  </div>
                </div>

                {detailAgr.line_items?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Line Items</h4>
                    <div className="bg-gray-50 rounded-lg divide-y divide-gray-200">
                      {detailAgr.line_items.map((item, i) => (
                        <div key={i} className="flex justify-between px-3 py-2 text-sm">
                          <span>{item.description}</span>
                          <span className="font-medium">{item.quantity} x ${item.unit_price} = ${item.amount}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Separator />

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-gray-700">Generated Invoices ({detailInvoices.length})</h4>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => handleGenerateInvoice(detailAgr)}
                      disabled={generating === detailAgr.id || !['active', 'draft'].includes(detailAgr.status)}
                      data-testid="detail-generate-invoice-btn"
                    >
                      <Receipt className="h-3.5 w-3.5 mr-1.5" /> Generate Invoice
                    </Button>
                  </div>
                  {detailInvoices.length === 0 ? (
                    <p className="text-sm text-gray-400">No invoices generated yet.</p>
                  ) : (
                    <div className="bg-gray-50 rounded-lg divide-y divide-gray-200">
                      {detailInvoices.map(inv => (
                        <div key={inv.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-gray-100 cursor-pointer transition-colors" onClick={() => navigate(`/invoices/${inv.id}`)} data-testid={`invoice-link-${inv.id}`}>
                          <div>
                            <span className="text-sm font-medium text-blue-600">#{inv.invoice_number}</span>
                            <span className="text-xs text-gray-500 ml-2">{inv.issue_date ? format(new Date(inv.issue_date), 'MMM d, yyyy') : ''}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={`text-xs ${inv.status === 'paid' ? 'bg-green-100 text-green-700' : inv.status === 'sent' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>{inv.status}</Badge>
                            <span className="text-sm font-semibold">${(inv.total || 0).toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Renew Dialog */}
      <Dialog open={renewDialogOpen} onOpenChange={setRenewDialogOpen}>
        <DialogContent className="max-w-md" data-testid="renew-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-green-600" />
              Renew Agreement
            </DialogTitle>
          </DialogHeader>
          {renewingAgr && (
            <div className="space-y-4 mt-2">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="text-sm font-semibold text-gray-900">{renewingAgr.name}</div>
                <div className="text-xs text-gray-500">{renewingAgr.customer_name}</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-gray-500">Type:</span> <Badge className={`ml-1 text-[10px] ${renewingAgr.type === 'SLA' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{renewingAgr.type}</Badge></div>
                  <div><span className="text-gray-500">Billing:</span> <span className="font-medium ml-1">{FREQ_LABELS[renewingAgr.billing_frequency]}</span></div>
                  <div><span className="text-gray-500">Current end:</span> <span className="font-medium text-red-600 ml-1">{renewingAgr.end_date}</span></div>
                  <div><span className="text-gray-500">Total:</span> <span className="font-bold ml-1">${(renewingAgr.total || 0).toLocaleString()}</span></div>
                </div>
                {renewingAgr.renewal_count > 0 && (
                  <div className="text-xs text-gray-400">Previously renewed {renewingAgr.renewal_count} time(s)</div>
                )}
              </div>

              <div>
                <Label>New End Date *</Label>
                <Input
                  type="date"
                  value={renewEndDate}
                  onChange={e => setRenewEndDate(e.target.value)}
                  min={renewingAgr.end_date}
                  data-testid="renew-end-date"
                />
                <p className="text-xs text-gray-400 mt-1">The agreement will be extended to this date with the same terms.</p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setRenewDialogOpen(false)}>Cancel</Button>
                <Button
                  onClick={handleRenew}
                  disabled={renewing || !renewEndDate}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="confirm-renew-btn"
                >
                  <RotateCcw className={`h-4 w-4 mr-1.5 ${renewing ? 'animate-spin' : ''}`} />
                  {renewing ? 'Renewing...' : 'Renew Agreement'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Template Create/Edit Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle>{editingTemplateId ? 'Edit Template' : 'Create Template'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Template Name *</Label>
                <Input value={templateForm.name} onChange={e => setTemplateForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Basic MSP Package" data-testid="template-name-input" />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={templateForm.type} onValueChange={v => setTemplateForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SLA">SLA</SelectItem>
                    <SelectItem value="MSA">MSA</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={templateForm.description} onChange={e => setTemplateForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="What this template covers..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Billing Frequency</Label>
                <Select value={templateForm.billing_frequency} onValueChange={v => setTemplateForm(f => ({ ...f, billing_frequency: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one-time">One-Time</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="semi-annual">Semi-Annual</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment Terms (days)</Label>
                <Input type="number" value={templateForm.payment_terms_days} onChange={e => setTemplateForm(f => ({ ...f, payment_terms_days: parseInt(e.target.value) || 30 }))} />
              </div>
            </div>

            <Separator />

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold">Line Items</Label>
                <Button variant="outline" size="sm" onClick={() => setTemplateForm(f => ({ ...f, line_items: [...f.line_items, { description: '', quantity: 1, unit_price: 0 }] }))} data-testid="template-add-item">
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                {templateForm.line_items.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2" data-testid={`template-line-${idx}`}>
                    <Input placeholder="Description" value={item.description} onChange={e => updateTemplateLineItem(idx, 'description', e.target.value)} className="flex-1" />
                    <Input type="number" placeholder="Qty" value={item.quantity} onChange={e => updateTemplateLineItem(idx, 'quantity', parseFloat(e.target.value) || 0)} className="w-20" />
                    <Input type="number" placeholder="Price" value={item.unit_price} onChange={e => updateTemplateLineItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} className="w-24" />
                    <span className="text-sm font-medium w-20 text-right">${(item.quantity * item.unit_price).toFixed(2)}</span>
                    {templateForm.line_items.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => setTemplateForm(f => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }))}>
                        <X className="h-3 w-3 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Tax Rate (%):</span>
                <Input type="number" value={templateForm.tax_rate} onChange={e => setTemplateForm(f => ({ ...f, tax_rate: parseFloat(e.target.value) || 0 }))} className="w-20 h-8 text-right" />
              </div>
              <span className="font-bold">Total: ${templateTotals.total.toFixed(2)}</span>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveTemplate} className="bg-blue-600 hover:bg-blue-700" data-testid="save-template-btn">
                {editingTemplateId ? 'Update Template' : 'Create Template'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
